/**
 * src/components/pdf/2024/sections/CoverAndAboutPDF.js
 *
 * Cover page + About Us & 2024 Highlights section.
 *
 * Named exports: CoverPage, TableOfContentsPage, AboutPage, CoverAndAboutSection
 * Default export: standalone Document (used by the generate script)
 */

import React from 'react';
import { Document, Page, View, Text, Image } from '@react-pdf/renderer';
import { styles, COLORS, registerFonts } from '../styles';
import PageTemplate from '../PageTemplate';
import {
  H2, H3, Body, Small, Section,
  BulletList, Blockquote, Footnote,
  PageOffsetContext,
} from '../shared';

// Brand background images (cover page)
import columbiaBkgImg from '../../../ui/corporateresponsibility/impact/columbia_impact-report_home.jpg';
import sorelBkgImg from '../../../ui/corporateresponsibility/impact/sorel_impact-report_home.jpg';
import mhwBkgImg from '../../../ui/corporateresponsibility/impact/mhw_impact-report_home.jpg';
import pranaBkgImg from '../../../ui/corporateresponsibility/impact/prana_impact-report_home.jpg';
import impactBadge from '../../../ui/corporateresponsibility/impact/impact_report_badge_2024.png';
//import mhwClimbImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/mhw_climb.jpg';
import empoweringPeopleHighlightsImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/empowering_people_highlights.jpg';
import sustainingPlacesHighlightsImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/sustaining_places_highlights_1.jpg';
import responsiblePracticesHighlightsImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/responsible_practices_highlights.jpg';

// Partnership logos
import aafaLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/aafa-logo.png';
import betterWorkLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/better-work-logo.png';
import canopyLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/canopy-logo.png';
import cascaleLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/cascale-logo-banner.png';
import lwgLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/lwg-logo.png';
import oiaLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/oia-logo.png';
import riseLogoImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/rise-logo.png';
import textileExchangeLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/textile-exchange-logo.png';

// Brand logos (white — cover page)
import columbiaLogoWhite from '../../../ui/brandlogos/columbia_logo.png';
import sorelLogoWhite from '../../../ui/brandlogos/sorel_logo.png';
import mhwLogoWhite from '../../../ui/brandlogos/mhw_logo.png';
import pranaLogoWhite from '../../../ui/brandlogos/prana_logo.png';
// Brand logos (black — about page)
import columbiaLogo from '../../../ui/brandlogos/columbia_logo_black.png';
import sorelLogo from '../../../ui/brandlogos/SOREL_logo_black.png';
import mhwLogo from '../../../ui/brandlogos/mhw_logo_black.png';
import pranaLogo from '../../../ui/brandlogos/prAna_logo_black.png';

import afirmLogo from '../../../ui/corporateresponsibility/responsiblepractices/OrganizationImages/afirm_logo.png';
import corpLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/corp_logo.png';
import fesiLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/fesi-logo.jpg';
import rilaLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/rila-logo.jpg';
import wfsgiLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/about/partnerships/WFSGI.jpeg';
//import { AbsoluteCenter } from '@chakra-ui/react';

registerFonts();

// ---------------------------------------------------------------------------
// Cover page
// ---------------------------------------------------------------------------
export function CoverPage() {
  return (
    <Page size="LETTER" orientation='landscape' style={[styles.coverPage]}>
      {/* 2x2 brand image grid */}
      <View style={{ flex: 1, flexDirection: 'column' }}>
        {/* Row 1: Columbia + SOREL */}
        <View style={{ flex: 1, flexDirection: 'row' }}>
          <View style={{ flex: 1, position: 'relative', marginRight: 0.5 }}>
            <Image src={columbiaBkgImg} style={styles.sectionCoverBg} />
            <View style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#000000', opacity: 0.4 }} />
            <Image src={columbiaLogoWhite} style={{ position: 'absolute', bottom: 8, left: 8, width: 110 }} />
          </View>
          <View style={{ flex: 1, position: 'relative', marginLeft: 0.5 }}>
            <Image src={sorelBkgImg} style={styles.sectionCoverBg} />
            <View style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#000000', opacity: 0.4 }} />
            <Image src={sorelLogoWhite} style={{ position: 'absolute', bottom: 8, right: 8, width: 65 }} />
          </View>
        </View>
        {/* Row 2: Mountain Hardwear + prAna */}
        <View style={{ flex: 1, flexDirection: 'row', marginTop: 0.5 }}>
          <View style={{ flex: 1, position: 'relative', marginRight: 0.5 }}>
            <Image src={mhwBkgImg} style={styles.sectionCoverBg} />
            <View style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#000000', opacity: 0.4 }} />
            <Image src={mhwLogoWhite} style={{ position: 'absolute', bottom: 44, left: 8, width: 90 }} />
          </View>
          <View style={{ flex: 1, position: 'relative', marginLeft: 0.5 }}>
            <Image src={pranaBkgImg} style={styles.sectionCoverBg} />
            <View style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#000000', opacity: 0.4 }} />
            <Image src={pranaLogoWhite} style={{ position: 'absolute', bottom: 44, right: 8, width: 65 }} />
          </View>
        </View>
      </View>
      {/* Impact badge centered over the grid */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', alignItems: 'center' }}>
        <Image src={impactBadge} style={{ width: 240 }} />
      </View>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Table of Contents
// Page numbers reflect section starts in the full assembled report.
// ---------------------------------------------------------------------------
export function TableOfContentsPage() {
  const entries = [
    {
      title: 'About Us & 2024 Highlights',
      description: 'Our mission, core values, brands, scope & scale, impact governance, and 2024 highlights.',
      page: 3,
      color: COLORS.navy,
    },
    {
      title: 'Empowering People',
      description: 'Employee wellbeing, talent development, Employee Resource Networks, supply chain workers, and community investment.',
      page: 8,
      color: COLORS.green,
    },
    {
      title: 'Sustaining Places',
      description: 'Climate & environmental management, sustainable manufacturing, materials, and packaging.',
      page: 23,
      color: COLORS.teal,
    },
    {
      title: 'Responsible Practices',
      description: 'Ethics & compliance, supply chain traceability, and social responsibility programs.',
      page: 31,
      color: COLORS.orange,
    },
    {
      title: 'Appendices',
      description: 'SASB index, GHG emissions data, audit & assessment metrics, scorecard ratings, and additional data tables.',
      page: 38,
      color: COLORS.midGray,
    },
  ];

  return (
    <PageTemplate sectionTitle="Table of Contents">
      <Text style={{ fontSize: 22, color: COLORS.navy, marginBottom: 32, marginTop: 16 }}>
        Table of Contents
      </Text>
      {entries.map((entry) => (
        <View
          key={entry.title}
          style={{
            flexDirection: 'row',
            alignItems: 'stretch',
            marginBottom: 10,
            borderBottomWidth: 0.5,
            borderBottomColor: COLORS.borderGray,
            borderBottomStyle: 'dotted',
            paddingBottom: 10,
          }}
        >
          {/* Colored accent bar */}
          <View style={{ width: 4, backgroundColor: entry.color, marginRight: 14, borderRadius: 2 }} />
          {/* Section info */}
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 13, color: COLORS.darkGray, marginBottom: 3, fontFamily: 'Helvetica-Bold' }}>
              {entry.title}
            </Text>
            <Text style={{ fontSize: 8.5, color: COLORS.midGray, lineHeight: 1.5 }}>
              {entry.description}
            </Text>
          </View>
          {/* Page number */}
          <View style={{ width: 40, justifyContent: 'center', alignItems: 'flex-end' }}>
            <Text style={{ fontSize: 20, color: entry.color, fontFamily: 'Helvetica-Bold' }}>
              {entry.page}
            </Text>
          </View>
        </View>
      ))}
    </PageTemplate>
  );
}

// ---------------------------------------------------------------------------
// About Us & 2024 Highlights
// ---------------------------------------------------------------------------
export function AboutPage() {
  return (
    <>
      {/* ── Mission ──────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="About Us & 2024 Highlights" hideHeader style={{ backgroundColor: COLORS.black }}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ fontSize: 10, color: COLORS.white, marginBottom: 24, fontFamily: 'Helvetica', textAlign: 'center', letterSpacing: 1.5, textTransform: 'uppercase' }}>
            Our Mission
          </Text>
          <Text style={{ fontSize: 48, color: COLORS.white, textAlign: 'center', fontFamily: 'Helvetica-Bold', lineHeight: 1.3 }}>
            We Connect Active People{'\n'}With Their Passions
          </Text>
        </View>
      </PageTemplate>

      {/* ── Core values & brands ─────────────────────────────────────── */}
      <PageTemplate sectionTitle="About Us & 2024 Highlights">
        <Section>
          <H2>Our Core Values</H2>
          <BulletList
            items={[
              'Enjoy an Active Lifestyle',
              'Compete to Win',
              'Relentless Improvement',
              'Culture of Honesty, Respect, & Trust',
              'Doing the Right Thing',
            ]}
          />
        </Section>

        <Section>
          <H2>Our Brands</H2>

          <Image src={columbiaLogo} style={{ width: 130, marginBottom: 4, marginTop: 12, objectFit: 'contain', objectPositionX: 0 }} />
          <Body>
            Founded in 1938, our Columbia brand's mission is to unlock the outdoors for everyone.
            The Columbia brand offers authentic, high-value outdoor apparel, footwear, accessories
            and equipment products suited for hiking, trail running, snow, and fishing and hunting
            activities, as well as everyday outdoor activities.
          </Body>

          <Image src={sorelLogo} style={{ width: 80, marginBottom: 4, marginTop: 8, objectFit: 'contain', objectPositionX: 0 }} />
          <Body>
            Acquired in 2000, our SOREL brand has evolved from a men's utility boot brand into a
            contemporary lifestyle brand, bringing style to the outdoors. SOREL leverages its rich
            heritage, innovation and style to offer distinct, compelling, and unexpected footwear to
            consumers around the world.
          </Body>

          <Image src={mhwLogo} style={{ width: 100, marginBottom: 4, marginTop: 8, objectFit: 'contain', objectPositionX: 0 }} />
          <Body>
            Acquired in 2003, our Mountain Hardwear brand's mission is to encourage and equip people
            to seek a wilder path in life. With over 30 years of wild wisdom, Mountain Hardwear
            continues to offer essential, premium apparel, accessories and equipment products for
            climbers, mountaineers, skiers, snowboarders, and trail athletes.
          </Body>

          <Image src={pranaLogo} style={{ width: 100, marginBottom: 4, marginTop: 8, objectFit: 'contain', objectPositionX: 0 }} />
          <Body>
            Acquired in 2014, our prAna brand's mission is to celebrate the spirit of adventure and
            discovery, encouraging individuals to pursue their passions inspired by an active
            California lifestyle. prAna offers apparel and accessories that blend style and
            versatility for individuals who embrace movement and have a deep reverence for nature.
          </Body>
        </Section>

        <Section>
          <H2>Message from Our CEO</H2>
          <Blockquote
            quote={'"At Columbia Sportswear Company, we believe in making a positive impact in the communities we live, work and recreate. Guided by our value of "doing the right thing," we strive to ensure our actions benefit both people and the planet. This report highlights our work around Empowering People, Sustaining Places and Responsible Practices."'}
            attribution="— Tim Boyle, Chairman, President & CEO"
          />
        </Section>
      </PageTemplate>

      {/* ── Scope & Scale ─────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="About Us & 2024 Highlights">
        <Section>
          <H2>2024 Scope & Scale*</H2>
          <View style={styles.statGrid}>
            <View style={[styles.statCell, { borderRightWidth: 1, borderRightColor: COLORS.borderGray }]}>
              <Text style={styles.statValue}>4</Text>
              <Text style={styles.statLabel}>Primary Brands</Text>
            </View>
            <View style={styles.statCell}>
              <Text style={styles.statValue}>1938</Text>
              <Text style={styles.statLabel}>Founded in Portland, Oregon</Text>
            </View>
            <View style={[styles.statCell, { borderRightWidth: 1, borderRightColor: COLORS.borderGray }]}>
              <Text style={styles.statValue}>9,700</Text>
              <Text style={styles.statLabel}>Full-time & Part-time Employees</Text>
            </View>
            <View style={styles.statCell}>
              <Text style={styles.statValue}>$3,369</Text>
              <Text style={styles.statLabel}>Million Net Sales in 2024</Text>
            </View>
            <View style={[styles.statCell, { borderRightWidth: 1, borderRightColor: COLORS.borderGray }]}>
              <Text style={styles.statValue}>170+</Text>
              <Text style={styles.statLabel}>Directly Operated U.S. Retail Stores</Text>
            </View>
            <View style={styles.statCell}>
              <Text style={styles.statValue}>110+</Text>
              <Text style={styles.statLabel}>Countries in Which Product is Sold</Text>
            </View>
          </View>
          <Small>*As of December 31, 2024</Small>
        </Section>

        <Section>
          <H2>Impact Governance</H2>
          <Body>
            Our Board of Directors (the Board) shares in the oversight of our impact efforts. The
            Talent and Compensation Committee considers human capital management issues, while the
            Audit Committee reviews periodic reporting on environmental, social, and governance
            (ESG) matters. The Nominating and Corporate Governance Committee considers ESG issues,
            including potential long- and short-term trends and impacts on Columbia Sportswear
            Company's ("CSC") business. Each committee reports to the full Board with respect to
            their specific responsibilities. The Board is also informed of supply chain oversight
            and related risks. CSC management views ESG as a company-wide effort, with
            collaboration and commitment throughout the supply chain. For more information, please
            visit our Investor Relations website for our Board Committee Charters. In 2021, the
            Company created an ESG Steering Committee comprised of senior leaders in various
            different functions.
          </Body>
        </Section>
        </PageTemplate>

      {/* ── 2024 Highlights ─────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="About Us & 2024 Highlights">
        <View style={{ justifyContent: 'center' }}>
          <Section>
            <H2>2024 Highlights</H2>
            <View style={{ flexDirection: 'row', gap: 24, marginTop: 16 }}>
              {/* Empowering People */}
              <View style={{ flex: 1, alignItems: 'center' }}>
                <Image src={empoweringPeopleHighlightsImg} style={{ width: 200, height: 200, objectFit: 'cover', borderRadius: 100, marginBottom: 8 }} />
                <H3>Empowering People</H3>
                <BulletList
                  textStyle={{ fontSize: 9.5 }}
                  items={[
                    "As of 2024, we've exceeded 80% of our Planet Water goal of providing access to clean water for 100,000 people per day.",
                    'Employees contributed over 6,100 volunteer hours.',
                    'Mountain Hardwear was named Corporate Partner of the Year by Leave No Trace.',
                    'The myWellbeing program reimbursed more than 1,300 unique requests for a variety of employee wellness activities.',
                  ]}
                />
              </View>
              {/* Sustaining Places */}
              <View style={{ flex: 1, alignItems: 'center' }}>
                <Image src={sustainingPlacesHighlightsImg} style={{ width: 200, height: 200, objectFit: 'cover', borderRadius: 100, marginBottom: 8 }} />
                <H3>Sustaining Places</H3>
                <BulletList
                textStyle={{ fontSize: 9.5 }}
                  items={[
                    'Absolute energy consumption, 2022 versus 2024, at our Oregon distribution center and headquarters has declined by over 900,000 kilowatt hours and nearly 67,000 therms\u00B9.',
                    'The Chinese Taipei office began a program to procure 100% Renewable Energy Certificates (RECs) to account for all grid energy consumption.',
                    'Global corrugated outer cartons and inner boxes used for product packaging contained a minimum of 95% recycled content in 2024.',
                  ]}
                />
              </View>
              {/* Responsible Practices */}
              <View style={{ flex: 1, alignItems: 'center' }}>
                <Image src={responsiblePracticesHighlightsImg} style={{ width: 200, height: 200, objectFit: 'cover', borderRadius: 100, marginBottom: 8 }} />
                <H3>Responsible Practices</H3>
                <BulletList
                textStyle={{ fontSize: 9.5 }}
                  items={[
                    'In July 2024, our internal Ethics & Compliance awareness month highlighted our Compliance Line to further encourage a speak-up culture.',
                    'In partnership with all other RISE industry supporters in 2024, RISE reached over 425 workplaces, totaling more than 375,000 workers, over 65% of whom identify as women.',
                    'In 2024, we worked with 330 finished good vendors, of which 87% completed at least one audit or assessment during the year.',
                  ]}
                />
              </View>
            </View>
          </Section>
        </View>
        <View style={[styles.footnoteSection, { marginTop: -4 }]}>
          <Footnote
            num={1}
            text="Absolute energy consumption is calculated as the sum of electricity and natural gas consumption at our Oregon distribution center and headquarters."
          />
        </View>
        </PageTemplate>

      {/* ── Industry Partnerships & Memberships ─────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="About Us & 2024 Highlights">
        <Section>
          <H2>Industry Partnerships & Memberships</H2>
          <Body>
            CSC actively participates in a wide range of industry partnerships and memberships that
            advance responsible business practices across the outdoor, apparel, and footwear
            sectors.
          </Body>
          <Blockquote
            quote={"\"Being a member of the Outdoor Industry Association (OIA) Board of Directors allows me to positively contribute to the industry\u2019s impact progress and mission to grow the benefits of the outdoors for everyone.\""}
            attribution="— Troy Sicotte, Mountain Hardwear Brand President"
          />
          <View style={styles.partnerLogoRow}>
            <View style={styles.partnerLogoGridRow}>
              <Image src={afirmLogo}      style={styles.partnerLogo_afirm} />
              <Image src={aafaLogo}       style={styles.partnerLogo_aafa} />
              <Image src={betterWorkLogo} style={styles.partnerLogo_betterWork} />
              <Image src={corpLogo}       style={styles.partnerLogo_corp} />
            </View>
            <View style={styles.partnerLogoGridRow}>
              <Image src={canopyLogo}  style={styles.partnerLogo_canopy} />
              <Image src={cascaleLogo} style={styles.partnerLogo_cascale} />
              <Image src={fesiLogo}    style={styles.partnerLogo_fesi} />
              <Image src={lwgLogo}     style={styles.partnerLogo_lwg} />
            </View>
            <View style={styles.partnerLogoGridRow}>
              <Image src={oiaLogo}      style={styles.partnerLogo_oia} />
              <Image src={rilaLogo}     style={styles.partnerLogo_rila} />
              <Image src={riseLogoImg}  style={styles.partnerLogo_rise} />
            </View>
            <View style={styles.partnerLogoGridRow}>
              <Image src={textileExchangeLogo} style={styles.partnerLogo_textileExchange} />
              <Image src={wfsgiLogo}           style={styles.partnerLogo_wfsgi} />
            </View>
          </View>
        </Section>
      </PageTemplate>
    </>
  );
}

// ---------------------------------------------------------------------------
// Section composite (for use in the full assembled ImpactReportPDF)
// ---------------------------------------------------------------------------
export function CoverAndAboutSection() {
  return (
    <>
      <CoverPage />
      <TableOfContentsPage />
      <AboutPage />
    </>
  );
}

// ---------------------------------------------------------------------------
// Standalone Document (used by the generate script)
// ---------------------------------------------------------------------------
export default function CoverAndAboutPDF({ pageOffset = 0, totalDocPages = null } = {}) {
  return (
    <PageOffsetContext.Provider value={{ pageOffset, totalDocPages }}>
      <Document
        title="2024 CSC Impact Report — Cover & About"
        author="Columbia Sportswear Company"
      >
        <CoverPage />
        <TableOfContentsPage />
        <AboutPage />
      </Document>
    </PageOffsetContext.Provider>
  );
}
